import React, { useState, useEffect, useMemo } from 'react';
import { useParams, NavLink, useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import GalleryLightbox from '../components/GalleryLightbox';
import ContactForm from '../components/ContactForm';
import { SERVICES as STATIC_SERVICES, COMPANY_INFO as STATIC_COMPANY_INFO } from '../data/siteData';
import { useServices, useCompanyInfo, useGalleryCategories, getServiceGalleryData } from '../lib/cms';

export default function ServiceDetailPage() {
  const { services: SERVICES } = useServices();
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const { categories: GALLERY_CATEGORIES } = useGalleryCategories();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);

  // Normalize slug matching (handle alias routes or query cases)
  const normalizedSlug = slug ? slug.toLowerCase() : '';
  const service = SERVICES.find(
    (s) =>
      s.slug === normalizedSlug ||
      (normalizedSlug === 'jord-gravearbejde' && s.slug === 'gravearbejde') ||
      (normalizedSlug === 'jordarbejde' && s.slug === 'gravearbejde') ||
      (normalizedSlug === 'anlaegsarbejde' && s.slug === 'anlaegsgartner') ||
      (normalizedSlug === 'have' && s.slug === 'belaegning-have') ||
      (normalizedSlug === 'haveservice' && s.slug === 'belaegning-have') ||
      (normalizedSlug === 'belaegning-og-have' && s.slug === 'belaegning-have') ||
      (normalizedSlug === 'vognmandskoersel' && s.slug === 'vognmand') ||
      (normalizedSlug === 'container-udlejning' && s.slug === 'containerudlejning')
  );

  useEffect(() => {
    if (!service && slug) {
      navigate('/404', { replace: true });
    }
  }, [service, slug, navigate]);

  // Hent dynamiske galleridata fra CMS for denne service med nyeste billeder først
  const galleryData = useMemo(() => {
    return getServiceGalleryData(service, GALLERY_CATEGORIES);
  }, [service, GALLERY_CATEGORIES]);

  // Find matchende hovedkategori i galleriet så "Se hele galleriet" linker direkte til det rette hovedgalleri
  const targetCategorySlug = useMemo(() => {
    if (!service) return 'all';
    const gSlug = (service.gallerySlug || '').toLowerCase();
    const sSlug = (service.slug || '').toLowerCase();
    const sCat = (service.category || '').toLowerCase();

    if (gSlug === 'jordarbejde' || gSlug === 'nedbrydning' || sSlug.includes('grave') || sSlug.includes('nedbryd') || sCat.includes('entreprenør')) {
      return 'entreprenoer';
    }
    if (gSlug === 'belaegning' || sSlug.includes('belaeg')) {
      return 'belaegning';
    }
    if (gSlug === 'anlaegsarbejde' || gSlug === 'haveservice' || sSlug.includes('have') || sSlug.includes('anlaeg') || sSlug.includes('rydning') || sCat.includes('have')) {
      return 'have';
    }
    if (gSlug === 'vognmandskoersel' || gSlug === 'containerudlejning' || sSlug.includes('vognmand') || sSlug.includes('container') || sSlug.includes('materiale') || sSlug.includes('bortskaffelse')) {
      return 'vognmand';
    }
    return 'all';
  }, [service]);

  const galleryLink = targetCategorySlug !== 'all' ? `/galleri?kategori=${targetCategorySlug}` : '/galleri';

  if (!service) return null;
  const relatedServices = SERVICES.filter((s) => s.slug !== service.slug);

  const openLightbox = (idx) => {
    setActiveIndex(idx);
    setLightboxOpen(true);
  };

  return (
    <>
      <SEO
        title={service.metaTitle || `${service.title} - CMSNord.dk`}
        description={service.metaDescription || service.shortDesc}
        canonicalUrl={`${window.location.origin}/ydelser/${service.slug}`}
        ogImage={service.primaryImage}
      />

      {/* Header Banner with high quality background image & dark overlay */}
      <section className="bg-[#121212] text-white py-16 sm:py-20 border-b border-zinc-800 relative overflow-hidden">
        {service.primaryImage && (
          <div className="absolute inset-0 z-0">
            <img
              src={service.primaryImage}
              alt={service.title}
              className="w-full h-full object-cover opacity-30 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/95 via-[#121212]/85 to-[#121212]/60" />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex items-center gap-2 text-xs text-[#F4AF00] font-bold uppercase tracking-widest mb-3 drop-shadow">
            <NavLink to="/ydelser" className="hover:underline">Ydelser</NavLink>
            <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
            <span>{service.title}</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black text-white mb-3 uppercase tracking-tight drop-shadow">
            {service.title}
          </h1>

          <p className="text-zinc-200 text-base max-w-3xl drop-shadow">
            {service.shortDesc}
          </p>
        </div>
      </section>

      {/* Main Content & Gallery */}
      <section className="py-12 sm:py-16 bg-[#F4F4F6] text-[#121212] border-b border-zinc-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Primary Column */}
            <div className="lg:col-span-8 space-y-8">
              {/* Primary Image */}
              {service.primaryImage && (
                <div className="rounded-xl overflow-hidden border border-zinc-200 shadow-sm h-72 sm:h-96">
                  <img
                    src={service.primaryImage}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Rich Body Content */}
              <div className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-10 text-zinc-800 leading-relaxed shadow-sm border-t-4 border-t-[#F4AF00] prose prose-zinc max-w-none prose-headings:font-bold prose-headings:text-[#121212] prose-headings:tracking-tight prose-h2:text-xl sm:prose-h2:text-2xl prose-h2:border-b prose-h2:border-zinc-100 prose-h2:pb-3 prose-h2:mt-8 prose-h2:first:mt-0 prose-h3:text-lg prose-ul:list-disc prose-ul:pl-5 prose-li:my-2 prose-li:text-zinc-700 prose-strong:text-[#121212]">
                <div dangerouslySetInnerHTML={{ __html: service.body }} />
              </div>

              {/* Gallery Section for this service */}
              {galleryData && galleryData.images && galleryData.images.length > 0 && (
                <div className="space-y-4 pt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <h3 className="text-xl font-black uppercase tracking-tight text-[#121212]">
                        Billeder fra {service.title}
                      </h3>
                    </div>
                    <Link
                      to={galleryLink}
                      className="text-xs sm:text-sm font-bold text-zinc-700 hover:text-[#121212] flex items-center gap-1.5 transition-colors self-start sm:self-auto"
                    >
                      <span>Se hele galleriet</span>
                      <i className="fa-solid fa-arrow-right text-xs text-[#F4AF00]" aria-hidden="true" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {galleryData.images.slice(0, 9).map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => openLightbox(idx)}
                        className="group relative h-40 rounded-xl overflow-hidden bg-white border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#F4AF00] cursor-pointer shadow-sm"
                        title={img.caption || service.title}
                      >
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${img.url.replace('https://assets.auxodev.dk/', '')}`}
                          alt={img.caption || service.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 rounded-xl"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-[#121212]/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <i className="fa-solid fa-magnifying-glass-plus text-[#F4AF00] text-xl" aria-hidden="true" />
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="pt-2 flex justify-start">
                    <Link
                      to={galleryLink}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-100 hover:bg-[#F4AF00] hover:text-[#121212] text-zinc-800 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-xl transition-all border border-zinc-200 shadow-sm"
                    >
                      <i className="fa-solid fa-images" aria-hidden="true" />
                      <span>Se alle billeder i galleriet</span>
                    </Link>
                  </div>
                </div>
              )}

              {/* Direct Call / Contact Box */}
              <div className="p-6 sm:p-8 rounded-xl bg-[#121212] border border-zinc-800 text-white flex flex-col sm:flex-row items-center justify-between gap-6 shadow-md border-l-4 border-l-[#F4AF00]">
                <div>
                  <h4 className="text-lg font-black text-white uppercase tracking-tight">Brug for {service.title}?</h4>
                  <p className="text-zinc-300 text-xs sm:text-sm mt-1">
                    Kontakt CMSNord.dk i dag for et uforpligtende tilbud på din opgave.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="nowrap-contact px-6 py-3 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-sm uppercase tracking-wider transition-all shadow-md flex items-center gap-2"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span>Ring {COMPANY_INFO.phone}</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-8">
              {/* Form Widget */}
              <ContactForm title={`Bestil ${service.title}`} />

              {/* Related Services */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                <h4 className="text-base font-bold text-[#121212] mb-4 pb-2 border-b border-zinc-100">
                  Alle Ydelser
                </h4>
                <div className="space-y-2">
                  {SERVICES.map((rel) => (
                    <NavLink
                      key={rel.slug}
                      to={`/ydelser/${rel.slug}`}
                      className={`block p-3 rounded-xl border transition-all text-xs sm:text-sm font-medium bg-zinc-50 border-zinc-200/80 text-zinc-700 hover:bg-[#F4AF00] hover:text-[#121212] hover:border-[#F4AF00] ${
                        rel.slug === service.slug ? 'font-bold border-zinc-300 text-zinc-950' : ''
                      }`}
                    >
                      {rel.title}
                    </NavLink>
                  ))}
                </div>
              </div>

              {/* Contact info widget */}
              <div className="bg-[#121212] text-white p-6 border border-zinc-800 shadow-md">
                <h4 className="text-sm font-black uppercase tracking-wider text-[#F4AF00] mb-3">
                  Kontakt Information
                </h4>
                <div className="space-y-3 text-xs text-zinc-300">
                  <div className="flex items-start gap-2.5">
                    <i className="fa-solid fa-location-dot text-[#F4AF00] mt-0.5" aria-hidden="true" />
                    <span>{COMPANY_INFO.fullAddress}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <i className="fa-solid fa-phone text-[#F4AF00]" aria-hidden="true" />
                    <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="hover:text-[#F4AF00] font-bold">
                      {COMPANY_INFO.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <i className="fa-solid fa-envelope text-[#F4AF00]" aria-hidden="true" />
                    <a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-[#F4AF00]">
                      {COMPANY_INFO.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <i className="fa-solid fa-clock text-[#F4AF00]" aria-hidden="true" />
                    <span>{COMPANY_INFO.hours}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {galleryData && (
        <GalleryLightbox
          images={galleryData.images}
          isOpen={lightboxOpen}
          initialIndex={activeImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
